import { Component } from "react";
import { Route, Router, Switch } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { AccessReportsRouter } from "@app/access-reports";
import { AuthenticationCallback, AuthenticationService, SilentRefreshCallback } from "@app/authentication";
import browserHistory from "@app/browserHistory";
import Home from "@app/home";
import Layout from "@app/layout";
import { MaintenancePage, MaintenanceRouter } from "@app/maintenance";
import MemberSetsRouter from "@app/members";
import ProviderSetsRouter from "@app/provider-sets";
import NotificationHandlerWrapper from "@app/NotificationHandlerWrapper";
import { provideNotificationContext } from "@app/notifications";
import { ProjectsRouter } from "@app/projects";
import { ProviderNetworkImportRouter } from "@app/provider-network-imports";
import { NotFound } from "@app/shared/ErrorPages";
import { TemplatesRouter } from "@app/templates";
import { ToolsRouter } from "@app/tools";
import { TutorialsRouter } from "@app/tutorials";
import { DashboardRestrictedRoute, DashboardType, InactivityMonitor, Roles, UserContext, UserInfo, UserService } from "@app/user";
import { AccountRouter } from "@app/user/account";
import { ErrorBoundary, LoadingSpinner } from "./shared";
import { TerminationsRouter } from "./terminations";
import { TagsRouter } from "./tags";
import { PerformanceRouter } from "./performance/PerformanceRouter";
import { ResourcesRouter } from "@app/resources";
import { Client, ClientService } from "@app/client";

import * as authentication from "@omnius/signalr/authentication";
import { DeploymentEnvironmentConstants } from "./shared/constants";
import { AdminRouter } from "./admin";
import TargetingRouter from "./targeting/TargetingRouter";
import { datadogRum } from "@datadog/browser-rum";
import { UnregisterCallback } from "history";
import { datadogViewNames } from "./datadogViewNames";
import { OperationsRouter } from "./operations";
import { UserClaim } from "@app/admin/user-administration/userModel";
import { NetworkOutreachRouter } from "@app/network-outreach";

import { withCookies, Cookies } from "react-cookie";
import { ReportsRouter } from "./reports/";
import OverviewsRouter from "./overviews/OverviewsRouter";
import { MappingContextProvider } from "./mapping/MappingContext";
import ExceptionsRouter from "./exceptions/ExceptionsRouter";
import { ManageReportPresets } from "./report-customization/manage-presets/ManageReportPresets";
import { FeatureFlagContext, Features } from "./feature-flags";

interface AppState {
  client: Client;
  marketoMunchkinReady: boolean,
  /** A way to tell that the user has been retrieved and we're ready to render
   * Otherwise we can't really tell whether the user still needs to be fetched or
   * it was an unauthorized attempt...since the user would be undefined in both cases
   * this keeps the 'unauthorized' page from flashing on screen while we're loading the user
   */
  ready: boolean;
  user: UserInfo;
}

interface AppProps {
  cookies: Cookies;
}

// "Munchkin" object is made available through the reference to munchkin.marketo.net/munchkin.js
// Because it's not explicitly defined, typescript throws a compile time error. This delcaration is meant to bypass that error
// Marketo also doesn't provide any typescript support, but here's their API: https://developers.marketo.com/javascript-api/lead-tracking/
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
declare const Munchkin: any;
const marketoMunchkinScriptId = "marketo-munchkin";

class App extends Component<AppProps, AppState> {
  private readonly userService = new UserService();
  private readonly clientService = new ClientService();
  private readonly sessionValidationIntervalSeconds = 60; // don't need to validate session too frequently
  private unlisten: UnregisterCallback;
  constructor(props: AppProps) {
    super(props);

    this.state = {
      client: undefined,
      marketoMunchkinReady: false,
      ready: false,
      user: undefined
    };
  }

  public async componentDidMount() {
    this.triggerFirstPendoSnippet();
    this.loadMarketoMunchkin();
    try {
      await AuthenticationService.getInstance().loadUser();
      // see if user is signed in. Prompts the user to sign in if not already
      // probably returns false if the user failed to sign in. Maybe.
      const signedIn = await AuthenticationService.getInstance().isUserSignedIn();

      if (!signedIn) { // not signed in
        // goofy special case - if the /callback or /silent-refresh-callback endpoint is being invoked, let it pass through without an authenticated user
        // otherwise, login. There's probably a better way to do this...
        if (window.location.pathname.indexOf("/callback") === -1 && window.location.pathname.indexOf("/silent-refresh-callback") === -1) {
          await AuthenticationService.getInstance().login();
        }

        this.setState({ client: undefined, ready: true, user: undefined });
        return;
      } else { // user is already logged in

        // not sure how else to accomplish this, but this gives the identity server time to sync up
        // with any changes made while the user was in 1.0 (e.g. switched to a guest user) before
        // validating the session
        setTimeout(async () => {
          await this.validateSession(); // will logout and redirect if session is not valid
        }, 2000);

        const user = await this.userService.get();
        const client = await this.clientService.getClient(user.clientId);

        const connection = new authentication.HubConnectionBuilder().getConnection();
        connection.subscribe("sessionEnded", this.handleSessionEnded);

        this.props.cookies.set("LastLoggedInDate", await this.userService.getLastLoginDate());

        this.setState(
          {
            client,
            //If loadDashboard is redirecting us to a new page, then there's no need to ready this page
            //Only load dashboard when navigating to the home page
            ready: (window.location.pathname === "/app/v2.0" ? !(await this.loadDashboard(user)) : true),
            user
          }, this.handleUserSet);
      }
    } catch (e) {
      console.log("App.tsx componentDidMount exception", e);
      this.setState({ client: undefined, ready: true, user: undefined });
    }

    // handles the initially loaded view
    datadogRum.startView(datadogViewNames(window.location.pathname));
    // and this will handle every view change
    this.unlisten = browserHistory.listen(({ pathname }) => {
      datadogRum.startView(datadogViewNames(pathname));
      this.logMarketoMunchkinPageVisit(pathname);
    });
  }

  public componentWillUnmount() {
    this.unlisten();
  }

  public handleSessionEnded(event: authentication.SessionEvent) {
    console.debug("Received a 'sessionEnded' notification:", event);
    const user = AuthenticationService.getInstance().getUser();
    if (user && event.userId.toString() === user.profile.sub && event.sessionId === user.profile.sid) {
      console.debug("Initiating Logout");
      AuthenticationService.getInstance().logout("InvalidSession");
    } else {
      console.debug("Ignoring 'sessionEnded' notification -- not my sessionId and/or userId");
    }
  }

  public render() {
    return this.state.user ?
      (
        <UserContext.Provider value={this.state.user}>
          <NotificationHandlerWrapper>
            <MappingContextProvider>
              {this.renderAppBody()}
            </MappingContextProvider>
            <InactivityMonitor />
          </NotificationHandlerWrapper>
        </UserContext.Provider>
      )
      :
      (
        <UserContext.Provider value={this.state.user}>
          {this.renderAppBody()}
        </UserContext.Provider>
      );
  }

  private renderAppBody() {
    if (this.state.user && this.state.user.showMaintenancePage && !this.state.user.roles.includes(Roles.MAINTENANCE_ADMIN)) {
      return (
        <MaintenancePage />
      );
    } else {
      return (
        <>
          <Router history={browserHistory}>
            <Layout>
              <ErrorBoundary user={this.state.user}>
                {this.state.ready ?
                  (
                    <FeatureFlagContext.Consumer>
                    {({ enabledFeatureFlags }) => (
                      <Switch>
                        <DashboardRestrictedRoute allowedDashboards={[DashboardType.NetworkAssessment]} exact={true} path="/" component={Home} />
                        <Route exact={true} path="/" component={Home} />
                        <Route exact={true} path="/callback" component={AuthenticationCallback} />
                        <Route exact={true} path="/silent-refresh-callback" component={SilentRefreshCallback} />
                        <Route path="/access-reports" component={AccessReportsRouter} />
                        <Route path="/account" component={AccountRouter} />
                        <Route path="/projects" component={ProjectsRouter} />
                        <Route path="/maintenance" component={MaintenanceRouter} />
                        <Route path="/member-populations" component={MemberSetsRouter} />
                        <Route path="/tutorials" component={TutorialsRouter} />
                        <Route path="/templates" component={TemplatesRouter} />
                        <Route path="/tools" component={ToolsRouter} />
                        <Route path="/targeting" component={TargetingRouter} />
                        <Route path="/tags" component={TagsRouter} />
                        <Route path="/terminations" component={TerminationsRouter} />
                        <Route path="/provider-network-import" component={ProviderNetworkImportRouter} />
                        <Route path="/reports" component={ReportsRouter} />
                        <Route path="/admin" component={AdminRouter} />
                        <Route path="/performance" component={PerformanceRouter} />
                        <Route path="/provider-sets" component={ProviderSetsRouter} />
                        <Route path="/overviews" component={OverviewsRouter} />
                        <DashboardRestrictedRoute allowedDashboards={[DashboardType.Operations]} path="/operations" component={OperationsRouter} />
                        <DashboardRestrictedRoute allowedDashboards={[DashboardType.NetworkOutreach]} path="/network-outreach" component={NetworkOutreachRouter} />
                        <Route path="/resources" component={ResourcesRouter} />
                        <Route path="/exceptions" component={ExceptionsRouter} />
                        {enabledFeatureFlags.has(Features.ADEQUACY_REPORT_CUSTOMIZATION) &&
                          <Route path="/report-presets" component={ManageReportPresets} />
                        }
                        <Route component={NotFound} />
                      </Switch>
                    )}
                    </FeatureFlagContext.Consumer>
                  )
                  : (
                    <div className="pt-3 pl-3">
                      <LoadingSpinner />
                    </div>
                  )
                }
              </ErrorBoundary>
              </Layout>
          </Router>

          <ToastContainer
            containerId="z-index-9999"
            closeOnClick={false}
            newestOnTop={true}
            pauseOnFocusLoss={false}
            position="top-right"
          />
          <ToastContainer
            containerId="z-index-1030"
            style={{ zIndex: 1030 }}
          />
        </>
      );
    }
  }

  private validateSession = async () => {
    let validSession = await this.userService.validateAndUpdateSession();

    if (!validSession) {
      await AuthenticationService.getInstance().renewToken();
      validSession = await this.userService.validateAndUpdateSession();

      if (!validSession) {
        console.warn("Session is not valid. Initiating logout.");
        AuthenticationService.getInstance().logout("InvalidSession");
        return;
      }
    }

    setTimeout(this.validateSession, this.sessionValidationIntervalSeconds * 1000);
  }

  private handleUserSet = () => {
    this.triggerSecondPendoSnippet();
    datadogRum.setUser({
      clientId: this.state.user.clientId,
      email: this.state.user.name,
      id: this.state.user.userId.toString(),
      name: this.state.user.clientName,
    });
  }

  private triggerFirstPendoSnippet = () => {
    if (window.environment !== DeploymentEnvironmentConstants.Local) {
      const snippet = document.createElement("script");
      snippet.innerHTML = `
        (function(apiKey){
              (function (p, e, n, d, o) {
                var v, w, x, y, z; o = p[d] = p[d] || {}; o._q = o._q || [];
                v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track']; for (w = 0, x = v.length; w < x; ++w)(function (m) {
                  o[m] = o[m] || function () { o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0))); };
                })(v[w]);
                y = e.createElement(n); y.async = !0; y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
                z = e.getElementsByTagName(n)[0]; z.parentNode.insertBefore(y, z);
              })(window, document, 'script', 'pendo');
        })('${window.pendoApiKey}');`;

      document.body.appendChild(snippet);
    }
  }

  private triggerSecondPendoSnippet = () => {
    if (window.environment !== DeploymentEnvironmentConstants.Local) {
      const snippet = document.createElement("script");
      snippet.innerHTML = `
        pendo.initialize({
          visitor: {
            id: '${this.state.user.userId}',
            email: '${this.state.user.email}',
            full_name: '${this.state.user.displayName}',
            environment: '${window.environment}',
            roles: '${this.state.user.roles.sort().join(", ")}',
            viewport_height: '${window.innerHeight}',
            viewport_width: '${window.innerWidth}',
          },
          account: {
            id: '${this.state.user.clientId}',
            name: '${this.state.user.clientName}',
            sf_account_id: '${this.state.client.sfAccountId}',
            isActive: '${this.state.client.isActive}',
            deactivated_date: '${!this.state.client.dateDeactivated ? "" : this.state.client.dateDeactivated}',
            roles: '${this.state.client.roles.map(r => r.name).sort().join(", ")}'
          }
        });`;
      document.body.appendChild(snippet);
    }
  }

  /**
   * Uses a combination of browser cookies and UserClaim to determine which dashboard to load for the user
   * After loading a dashboard, the user's selection is stored in cookies and in UserClaim
   * @param user
   * @return true/false to indicate if we're redirecting to a new page to get to the specified dashboard
   */
  private loadDashboard = async (user: UserInfo) => {
    const dashboardDefaultClaimType = "http://questanalytics.com/claims/dashboarddefault";

    let currentDashboard: DashboardType = DashboardType.Unknown;
    if (this.props.cookies.get("CurrentDashboard") in DashboardType) {
      currentDashboard = this.props.cookies.get("CurrentDashboard") as DashboardType;
    }

    let dashClaim: UserClaim = null;
    if (currentDashboard === DashboardType.Unknown) {
      let userClaims = await this.userService.getUserClaims();

      if (userClaims.length > 0)
        userClaims = userClaims.filter(x => x.type === dashboardDefaultClaimType);

      if (userClaims.length > 0)
        dashClaim = userClaims.find(x => x.value in DashboardType);

      if (!!dashClaim) {
        currentDashboard = dashClaim.value as DashboardType;
      } else {
        currentDashboard = this.getDefaultDashboard(user);
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    let requestedDashboard: DashboardType = DashboardType.Unknown;
    if (urlParams.get("Filter") in DashboardType) {
      requestedDashboard = urlParams.get("Filter") as DashboardType
    }

    if (requestedDashboard !== DashboardType.Unknown) {
      currentDashboard = requestedDashboard;
    }

    let path = "";
    let reroute = false;
    switch (currentDashboard) {
      case DashboardType.NetworkAssessment:
        path = "/";
        break;
      case DashboardType.Operations:
        path = "/operations/dashboard";
        break;
      case DashboardType.AccessReports:
        path = "/access-reports";
        break;
      case DashboardType.NetworkOutreach:
        path = "/network-outreach"
        break;
      case DashboardType.Unknown:
        path = window.location.pathname;
        break;
      case DashboardType.CACDI:
      case DashboardType.Directory:
      case DashboardType.NYSDOHAdHocProject:
      case DashboardType.Project:
        path = `${window.uiEndpoint}/Home?Filter=${currentDashboard}`;
        reroute = true;
        break;
      default:
        path = `${window.customUIBaseUrl}?Filter=${currentDashboard}`;
        reroute = true;
        break;
    }

    if (currentDashboard !== DashboardType.Unknown) {
      this.props.cookies.set("CurrentDashboard", currentDashboard, { path: "/" });
      await this.userService.removeUserClaim({ type: dashboardDefaultClaimType, value: currentDashboard });
      await this.userService.addUserClaim({ type: dashboardDefaultClaimType, value: currentDashboard });
    }

    if (reroute) {
      window.location.href = path;
      //Marketo munchkin logging handled in 1.0
    } else if (!!path) {
      browserHistory.replace(path, { isActive: true });
      this.logMarketoMunchkinPageVisit(path);
    }

    return reroute;
  }

  private getDefaultDashboard = (user: UserInfo) => {
    let defaultDashboard = DashboardType.Unknown;

    if (user.authorizedDashboardTypes.length == 0)
      return defaultDashboard;

    const projDash = user.authorizedDashboardTypes.find(x => x === DashboardType.Project);
    const netAssessDash = user.authorizedDashboardTypes.find(x => x === DashboardType.NetworkAssessment);

    if (!!netAssessDash)
      defaultDashboard = netAssessDash;
    else if (!!projDash)
      defaultDashboard = projDash;
    else
      defaultDashboard = user.authorizedDashboardTypes[0]

    return defaultDashboard
  }

  private checkMarketoMunchkinReady = () => {
    if (this.state.marketoMunchkinReady)
      return this.state.marketoMunchkinReady;

    const ready = !!document.getElementById(marketoMunchkinScriptId);

    this.setState({ marketoMunchkinReady: ready });

    return ready;
  }

  /**
   * Loads marketo munchkin js files into the page header if they're not already loaded there
   * Once loaded, a page visit will be logged to marketo
   */
  private loadMarketoMunchkin = () => {
    if (!window.enableMarketoMunchkinLogging)
      return;

    if (this.checkMarketoMunchkinReady())
      return;

    const script = document.createElement('script');
    script.id = marketoMunchkinScriptId
    script.type = 'text/javascript';
    script.async = true;
    script.src = '//munchkin.marketo.net/munchkin.js';
    script.onload = () => {
      //Setting apiOnly to true will ensure that page visits won't be automatically logged on init
      //Instead, logs will be controlled by logMarketoMunchkinPageVisit
      const config = { apiOnly: true };
      if (typeof (Munchkin) !== "undefined" && Munchkin !== null) {
        Munchkin.init(window.marketoMunchkinId, config);
        this.setState({ marketoMunchkinReady: true });
        this.logMarketoMunchkinPageVisit(window.location.pathname);
      }
    }
    document.getElementsByTagName('head')[0].appendChild(script);
  }

  private logMarketoMunchkinPageVisit = (pathname: string) => {
    if (!window.enableMarketoMunchkinLogging)
      return;

    if (!pathname || pathname.indexOf("/callback") !== -1 || pathname.indexOf("/silent-refresh-callback") !== -1)
      return;

    if (this.checkMarketoMunchkinReady() && (typeof (Munchkin) !== "undefined" && Munchkin !== null)) {
      Munchkin.munchkinFunction("visitWebPage", { href: pathname });
    } else {
      this.loadMarketoMunchkin();
    }
  }
}

export default provideNotificationContext(withCookies(App));
